import { FlagOutlined, FormOutlined } from '@ant-design/icons'
import { Switch, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { Badge, SectionLoadingState } from '../../../../components'
import {
  BadgeColors,
  ConformityCheckLabQrStatus,
  NO_DATA_PLACEHOLDER,
  OfrBlockType,
  QualityReviewDeviationType,
  conformityCheckLabQrStatuses,
  ofrBlockTypeNames,
  qualityReviewDeviationNames,
  workspaceDocumentSetStatuses,
} from '../../../../constants/constants'
import { useAlgorithmRunDetails } from '../../../../hooks'
import useWorkspaceDetails from '../../../../hooks/useWorkspaceDetails'
import notificationService from '../../../../services/notificationService'
import {
  ConformityCheckLabQRRunResult,
  ConformityCheckQualityReviewItem,
  DeviatingOfrBlock,
} from '../../../../store/reducers/workspaceReducer'
import { SmartFunctionListPanelLayout } from '../../components/SmartFunctionListPanelLayout'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 224px);
`

const StyledTable = styled(Table)`
  height: 100%;
  display: flex;
  flex-direction: column;
  .ant-table-cell {
    vertical-align: top !important;
    padding-top: 8px !important;
  }

  .ant-badge {
    margin-top: 0;
  }

  .deviation-row td {
    background-color: ${({ theme }) => theme.token.colorBgContainer} !important;
    border-top: 2px solid ${({ theme }) => theme.token.colorWarningBorder} !important;
    border-bottom: 2px solid ${({ theme }) => theme.token.colorWarningBorder} !important;
  }

  .deviation-row td:first-child {
    border-left: 2px solid ${({ theme }) => theme.token.colorWarningBorder} !important;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  .deviation-row td:last-child {
    border-right: 2px solid ${({ theme }) => theme.token.colorWarningBorder} !important;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`

const BadgeContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 0;
`
const DeviationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const { Text } = Typography

const DeviationText = styled(Text)<{ $isDeviation: boolean }>`
  margin-left: 8px;
  color: ${({ $isDeviation, theme }) =>
    $isDeviation ? theme.token.colorText : theme.token.colorTextDisabled};
`

const NOTIFICATION_DURATION = 5000

interface ConformityCheckQualityReviewSmartFunctionDetailsPagePanelProps {
  filter?: ConformityCheckLabQrStatus
  deviations?: ConformityCheckQualityReviewItem[]
  onDeviationToggle: (
    record: ConformityCheckQualityReviewItem,
    value: boolean
  ) => void
}

const ConformityCheckQualityReviewSmartFunctionDetailsPagePanel = ({
  filter,
  deviations,
  onDeviationToggle,
}: ConformityCheckQualityReviewSmartFunctionDetailsPagePanelProps) => {
  const { workspaceId, algorithmRunId, documentId } = useParams()
  const qualityReviewDeviationMapping = qualityReviewDeviationNames
  const qualityReviewOfrBlockTypeMapping = ofrBlockTypeNames
  const [isDeviationNotificationVisible, setIsDeviationNotificationVisible] =
    useState(false)

  useEffect(() => {
    if (algorithmRunId) {
      // Reset notification state when run changes
      setIsDeviationNotificationVisible(false)
    }
  }, [algorithmRunId])

  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const { loading, selectedAlgorithmRun } = useAlgorithmRunDetails({
    workspaceId,
    algorithmRunId,
    preventFetch: true,
  })

  const isDocumentClosed = useMemo(() => {
    const documentSetStatus =
      selectedWorkspace?.config?.conformityChecksConfig?.documents?.find(
        (doc) => doc.id === documentId
      )?.documentSetStatus

    return documentSetStatus === workspaceDocumentSetStatuses.CLOSED
  }, [selectedWorkspace, documentId])

  const flaggedDeviationDict:
    | { [id: string]: ConformityCheckQualityReviewItem }
    | undefined = useMemo(
    () =>
      selectedWorkspace?.config?.conformityChecksConfig?.flaggedDeviations?.reduce(
        (
          dict: { [id: string]: ConformityCheckQualityReviewItem },
          deviation
        ) => {
          if (
            deviation.algorithmRunId === algorithmRunId &&
            deviation.documentId === documentId
          ) {
            dict[deviation.id] = deviation
          }
          return dict
        },
        {}
      ),
    [selectedWorkspace, algorithmRunId, documentId]
  )

  const formattedData: ConformityCheckQualityReviewItem[] = useMemo(() => {
    const runResult =
      selectedAlgorithmRun?.runResult as ConformityCheckLabQRRunResult

    if (!runResult) return []

    const filteredData = Object.values(runResult).filter(
      (item: DeviatingOfrBlock) => {
        switch (filter) {
          case conformityCheckLabQrStatuses.CORRECTED:
            return item.status === conformityCheckLabQrStatuses.CORRECTED
          case conformityCheckLabQrStatuses.NOT_CORRECTED:
            return item.status !== conformityCheckLabQrStatuses.CORRECTED
          default:
            return true
        }
      }
    )

    const results = filteredData.map((item: DeviatingOfrBlock) => {
      const existingDeviation = deviations?.find(
        (deviation) =>
          deviation.id === item.id &&
          deviation.algorithmRunId === algorithmRunId &&
          deviation.documentId === documentId
      )
      const isDeviation = !!existingDeviation && existingDeviation?.isDeviation
      const isFlaggedDeviation = !!(
        flaggedDeviationDict && flaggedDeviationDict[item.id]
      )

      return {
        id: item?.id || '',
        page: item?.pageNum || '',
        ofrBlockType: item?.ofrBlockType || '',
        ofrBlockName: item?.ofrBlockName || '',
        status: item?.status || '',
        key: `${item?.id || ''}-${item?.ofrBlockName}`,
        pageNum: item?.pageNum || '',
        qualityReviewDeviation: item?.qualityReviewDeviation || '',
        isDeviation,
        algorithmRunId: algorithmRunId || '',
        documentId: documentId || '',
        isFlaggedDeviation,
      } as ConformityCheckQualityReviewItem
    })

    return results
  }, [
    selectedAlgorithmRun?.runResult,
    filter,
    algorithmRunId,
    documentId,
    deviations,
    flaggedDeviationDict,
  ])

  const handleDeviationToggle = useCallback(
    (record: ConformityCheckQualityReviewItem, value: boolean) => {
      if (onDeviationToggle) {
        onDeviationToggle(record, value)
      }
    },
    [onDeviationToggle]
  )

  useEffect(() => {
    const hasDeviations = deviations?.some(
      (deviation) =>
        deviation.documentId === documentId &&
        deviation.isDeviation &&
        deviation.algorithmRunId === algorithmRunId
    )

    const hasUnflaggedDeviations = deviations?.some(
      (deviation) =>
        deviation.documentId === documentId &&
        deviation.isDeviation &&
        deviation.algorithmRunId === algorithmRunId &&
        !flaggedDeviationDict?.[deviation.id]
    )

    const isDocumentClosed =
      selectedWorkspace?.config?.conformityChecksConfig?.documents?.find(
        (doc) => doc.id === documentId
      )?.documentSetStatus === workspaceDocumentSetStatuses.CLOSED

    if (isDocumentClosed) {
      return
    }

    if (hasDeviations && hasUnflaggedDeviations) {
      if (!isDeviationNotificationVisible) {
        notificationService.notificationWarning({
          messageToDisplay: t(
            'smartFunctionDetailsPage.qualityReviewDeviationNotification'
          ),
          duration: NOTIFICATION_DURATION,
          closable: true,
        })
        setIsDeviationNotificationVisible(true)
      }
    }
  }, [
    deviations,
    isDeviationNotificationVisible,
    documentId,
    algorithmRunId,
    selectedWorkspace,
    flaggedDeviationDict,
    selectedAlgorithmRun,
  ])

  const columns: ColumnsType<ConformityCheckQualityReviewItem> = [
    {
      title: t(
        'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.fieldName'
      ),
      dataIndex: 'ofrBlockName',
      key: 'ofrBlockName',
      width: '30%',
      render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
    },
    {
      title: t(
        'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.deviationType'
      ),
      dataIndex: 'qualityReviewDeviation',
      key: 'qualityReviewDeviation',
      width: '15%',
      render: (qualityReviewDeviation: string) => {
        const isValidDeviation =
          qualityReviewDeviation in qualityReviewDeviationMapping
        return (
          <>
            {isValidDeviation
              ? qualityReviewDeviationMapping[
                  qualityReviewDeviation as QualityReviewDeviationType
                ]
              : NO_DATA_PLACEHOLDER}
          </>
        )
      },
    },
    {
      title: t(
        'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.annotationType'
      ),
      dataIndex: 'ofrBlockType',
      key: 'ofrBlockType',
      width: '15%',
      render: (value) => {
        const isValidType = value in qualityReviewOfrBlockTypeMapping
        return (
          <>
            {isValidType
              ? qualityReviewOfrBlockTypeMapping[value as OfrBlockType]
              : NO_DATA_PLACEHOLDER}
          </>
        )
      },
    },
    {
      title: t(
        'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.pageNumber'
      ),
      dataIndex: 'pageNum',
      key: 'pageNum',
      width: '15%',
      render: (text) => <>{text || NO_DATA_PLACEHOLDER}</>,
    },
    {
      title: t(
        'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.status'
      ),
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (text, record) => {
        return (
          <BadgeContainer>
            {record.isFlaggedDeviation ? (
              <Badge icon={<FlagOutlined />} color={BadgeColors.WARNING}>
                {t('smartFunctionDetailsPage.labQrStatuses.flagged')}
              </Badge>
            ) : text ? (
              <Badge icon={<FormOutlined />} color={BadgeColors.SUCCESS}>
                {text || t('smartFunctionDetailsPage.labQrStatuses.corrected')}
              </Badge>
            ) : (
              NO_DATA_PLACEHOLDER
            )}
          </BadgeContainer>
        )
      },
    },
    {
      title: t(
        'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.deviation'
      ),
      dataIndex: 'isDeviation',
      key: 'isDeviation',
      width: '15%',
      render: (isDeviation: boolean, record) => (
        <DeviationContainer>
          <Switch
            checked={isDeviation}
            onChange={(value) => handleDeviationToggle(record, value)}
            disabled={
              isDocumentClosed ||
              record.status === conformityCheckLabQrStatuses.CORRECTED
            }
          />
          <DeviationText $isDeviation={isDeviation}>
            {isDeviation
              ? t(
                  'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.deviation'
                )
              : t(
                  'smartFunctionDetailsPage.qualityReviewDocumentAnnotationsTableTitles.ignored'
                )}
          </DeviationText>
        </DeviationContainer>
      ),
    },
  ]

  return loading ? (
    <SectionLoadingStateContainer>
      <SectionLoadingState />
    </SectionLoadingStateContainer>
  ) : (
    <SmartFunctionListPanelLayout>
      <StyledTable
        columns={columns}
        dataSource={formattedData}
        loading={loading}
        rowKey="key"
        pagination={false}
        scroll={{ y: 'calc(100vh - 190px)' }}
        rowClassName={(record) => {
          return record.isDeviation ? 'deviation-row' : ''
        }}
      />
    </SmartFunctionListPanelLayout>
  )
}

export default ConformityCheckQualityReviewSmartFunctionDetailsPagePanel
